import GeoJSONLayer from '@arcgis/core/layers/GeoJSONLayer.js';
import type MapView from '@arcgis/core/views/MapView';
import VectorTileLayer from '@arcgis/core/layers/VectorTileLayer';
import type { SpriteSourceImageInfo } from '@arcgis/core/layers/support/types';
import { whenOnce } from '@arcgis/core/core/reactiveUtils.js';

import {
	layerToLabelClass,
	VectorTileStyleToEsriRenderer,
} from '../utils/VectorTileStyleToEsriRenderer';
import { logVectorTileLayerStyleCall } from '../utils/logVectorTileLayerStyleCall';
import { updateStlyeLabelConfig, zoomToScale } from '../utils/rendererHelpers';

import { GeoJsonDataType, IconSet } from '@Map/services/types';
import { LayerVisibility } from '@Map/types';
import { getLayerIdFromIdPath, notUndefined, partition } from '@Map/utils';

import CompositeLayer from './CompositeLayer';
import { CompositeLayerProps } from '@Map/layers/types';
import logger from '@Map/logger/Logger';
import {
	FillLayerSpecification,
	LayerSpecification,
	LineLayerSpecification,
} from 'mapbox-gl';
import SymbolLoader from '@Map/symbols/SymbolLoader';

export interface DataLayer {
	displayName: string | undefined;
	active: boolean;
	id: string;
	icon: string | undefined;
	loaded: boolean;
	errored: boolean;
	color: string;
}

export interface LayerDebugInfo {
	id: string;
	layerName: string;
	tileSource?: string;
	tileLayerName?: string;
	dataServiceId: string;
	icon?: string;
	color: string;
	selectedColor?: string;
	type: string;
	loaded: boolean;
	errored: boolean;
	visible: boolean;
}

export type MapHiddenLayers = string[];

const mapToId = ({ id }: CompositeLayer | CompositeLayerProps): string => id;

export default class EsriLayerManager {
	private _layers: CompositeLayer[] = [];
	private _mapView: MapView;
	private _styleSources: any[] = [];
	/** VectorTileLayers created per style source; removed and recreated when style sources change. */
	private _vectorTileLayersCreated: VectorTileLayer[] = [];
	/**
	 * Tracks a monotonically increasing load sequence number per VectorTileLayer.
	 * Used so that `refreshLayerVisibility()` is only called from the *latest*
	 * `loadStyle` completion — earlier in-flight calls skip the refresh to avoid
	 * a race where `getStyleLayer` returns null because a newer `loadStyle` has
	 * already cleared the style.
	 */
	private _vtlLoadCounters = new Map<VectorTileLayer, number>();
	/** Serialises concurrent sprite refreshes per VTL (prevents AbortErrors). */
	private _vtlSpriteRefreshQueue = new Map<VectorTileLayer, { promise: Promise<void>; pending: boolean }>();
	/**
	 * Sprite ids currently resident in each VTL's atlas (the last successful
	 * `setSpriteSource`). Lets `ensureSpritesForStyleLayers` skip a rebuild +
	 * `vtl.refresh()` when every sprite a colour-by target needs is already
	 * present — collapsing the host's burst of re-applies into a single swap.
	 */
	private _vtlAtlasSpriteIds = new Map<VectorTileLayer, Set<string>>();
	/**
	 * Optional callback invoked after the *latest* `loadStyle` for a given VTL
	 * settles (i.e. no newer call is in flight).  Consumers can use this to
	 * re-apply an authoritative visibility state from outside the manager.
	 */
	private _onFinalStyleLoaded?: () => void;

	set onFinalStyleLoaded(cb: () => void) {
		this._onFinalStyleLoaded = cb;
	}

	/**
	 * Optional hook that lets the theme layer rewrite a style layer as it is
	 * built into the VTL style (before `loadStyle`). Used to bake the sensor
	 * heatmap "bubble" glow into the loaded style so the asset icon never paints
	 * first (no flicker) and the glow survives every style rebuild (e.g. save).
	 * Returns a replacement style layer, or null to build the layer normally.
	 */
	private _sensorStyleResolver?: (
		layerId: string,
		styleLayer: Record<string, any>,
	) => Record<string, any> | null;

	set sensorStyleResolver(
		fn:
			| ((
					layerId: string,
					styleLayer: Record<string, any>,
			  ) => Record<string, any> | null)
			| undefined,
	) {
		this._sensorStyleResolver = fn;
	}
	/** Selections queued before the target layer was registered. Applied in addOne. */
	private _pendingSelections = new Map<string, string[]>();
	/** Highlights queued before the target layer was registered. Applied in addOne. */
	private _pendingHighlights = new Map<string, string[]>();
	/** Tracks the last GeoJSON data reference used per layer, for change detection. */
	private _geoJsonLayerData = new Map<string, unknown>();

	constructor(mapView: MapView) {
		this._mapView = mapView;
	}

	get layers(): DataLayer[] {
		return this._layers.map(
			({ displayName, active, id, icon, loaded, errored, color }) => ({
				displayName,
				active: active ?? false,
				id,
				icon,
				loaded,
				errored,
				color,
			}),
		);
	}

	get debugInfo(): LayerDebugInfo[] {
		return this._layers.map(
			({
				id,
				displayName,
				source,
				sourceLayer,
				serviceId,
				icon,
				color,
				selectedColor,
				type,
				loaded,
				errored,
				active,
				onMap,
			}) => ({
				id,
				layerName: displayName,
				tileSource: sourceLayer ? source : undefined,
				tileLayerName: sourceLayer,
				dataServiceId: serviceId,
				icon,
				color,
				selectedColor,
				type,
				loaded,
				errored,
				visible: active ?? false,
				onMap,
			}),
		);
	}

	get compositeLayers(): CompositeLayer[] {
		return this._layers;
	}

	get layerIds(): string[] {
		return this._layers.map(mapToId);
	}

	get loadedLayers(): CompositeLayer[] {
		return this._layers.filter(({ loaded }) => loaded);
	}

	get settledLayers(): CompositeLayer[] {
		return this._layers.filter(({ loaded, errored }) => loaded || errored);
	}

	/** SymbolLoader with default icons plus merged theme {@link IconSet} from all composite layers. */
	private _symbolLoaderForSprites(): SymbolLoader {
		const merged = this._layers.reduce<IconSet>(
			(acc, layer) => ({ ...acc, ...layer.iconSet }),
			{},
		);
		return new SymbolLoader(merged);
	}

	get loadedLayerIds(): string[] {
		return this._layers.filter(({ loaded }) => loaded).map(mapToId);
	}

	get selectableLayerIdsOnMap(): string[] {
		return this._layers
			.filter(({ onMap, selectable }) => onMap && selectable)
			.map(mapToId);
	}

	get selectedLayerIds(): string[] {
		return this._layers.map(({ selectedId }) => selectedId);
	}

	get hiddenLayers(): MapHiddenLayers {
		return this._layers.filter(({ active }) => !active).map(mapToId);
	}

	set styleSources(sources: any[]) {
		this._styleSources = sources;
		this._updateEsriLayers();
	}

	getLayerIdByAssetType(
		systemType: string,
		assetType: string,
	): string | null {
		const layer = this._layers.find(layer =>
			systemType === 'SpatialData'
				? querySpatialLayer(layer, assetType)
				: systemType === 'Risk'
					? queryRiskLayer(layer, assetType)
					: queryAssetLayer(layer, systemType, assetType),
		);

		if (!layer) {
			console.warn('No layer found for', systemType, assetType);
			return null;
		}

		return layer.id;
	}

	getByServiceId(serviceId: string): CompositeLayer[] {
		return this._layers.filter(layer => layer.serviceId === serviceId);
	}

	private addOne(
		layerInfo: CompositeLayerProps,
		oneOfMany = false,
	): CompositeLayer | undefined {
		const layerId = layerInfo.id;
		if (this.layerIds.includes(layerId)) {
			this._update(layerId, layerInfo);
			return;
		}

		const layer = new CompositeLayer(layerInfo);
		if (layer.invalid) {
			logger.debug('Invalid layer', layerInfo);
			return;
		}
		// Wire the MapView so LayerBase._setVisibility can fall back to
		// map-iteration when its cached VectorTileLayer ref is missing or wrong.
		layer.view = this._mapView;
		if (this._pendingSelections.has(layerId)) {
			layer.selectedItems = this._pendingSelections.get(layerId)!;
			this._pendingSelections.delete(layerId);
		}
		if (this._pendingHighlights.has(layerId)) {
			layer.highlightedItems = this._pendingHighlights.get(layerId)!;
			this._pendingHighlights.delete(layerId);
		}
		if (!oneOfMany) {
			this._layers.push(layer);
		}
		return layer;
	}

	addMany(layers: CompositeLayerProps[], serviceId?: string): void {
		if (!Array.isArray(layers)) {
			if (serviceId) {
				this.deleteByServiceId(serviceId);
			} else {
				this.deleteAll();
			}
			return;
		}

		const layerIds = serviceId
			? this.getByServiceId(serviceId).map(mapToId)
			: this.layerIds;

		const [existingLayers, newLayers] = partition(layers, layer =>
			layerIds.includes(layer.id),
		);

		logger.debug('layers to update', existingLayers);
		logger.debug('layers to add', newLayers);

		existingLayers.forEach(layer => this._update(layer.id, layer));

		this._layers.push(
			...newLayers
				.map(layer => this.addOne(layer, true))
				.filter(notUndefined),
		);

		this._deleteNotFound(layers, serviceId);

		logger.debug('layers after update', this.debugInfo);
		this._updateEsriLayers();
	}

	private _updateEsriLayers() {
		if (
			!this._styleSources?.length ||
			!this._layers.length ||
			!this._mapView
		) {
			return;
		}

		const geoJsonSources: any[] = [];
		const vectorTileSourceItems: any[] = [];

		this._styleSources.forEach(item => {
			Object.entries(item).forEach(([, value]) => {
				if ((value as any).type === 'geojson') {
					geoJsonSources.push(item);
				}
				if ((value as any).type === 'vector') {
					// Dedupe: one entry per item that contains a vector source
					if (!vectorTileSourceItems.includes(item)) {
						vectorTileSourceItems.push(item);
					}
				}
			});
		});

		const geoJsonStyleLayers: any[] = [];
		this._layers.forEach(lyr => {
			if (
				geoJsonSources.some(item =>
					Object.keys(item).includes(lyr.source),
				)
			) {
				geoJsonStyleLayers.push(lyr.styleLayer);
			}
		});

		this._updateVectorTileLayers(vectorTileSourceItems);
		void this._updateGeoJsonLayers(
			geoJsonStyleLayers,
			geoJsonSources,
		).catch(err => {
			console.error('Error updating GeoJSON layers', err);
		});
	}

	private async _updateVectorTileLayers(
		vectorTileSourceItems: any[],
	): Promise<void> {
		// Only remove VTLs whose sourceId is no longer in the incoming list.
		// Keeping active layers on the map lets findLayerById detect them below.
		const newSourceIds = new Set(
			vectorTileSourceItems
				.map(item => Object.keys(item)[0])
				.filter(Boolean),
		);
		const toRemove = this._vectorTileLayersCreated.filter(
			vtl => !newSourceIds.has(vtl.id),
		);
		toRemove.forEach(vtl => {
			this._mapView.map?.remove(vtl);
			this._vtlLoadCounters.delete(vtl);
			this._vtlSpriteRefreshQueue.delete(vtl);
			// VectorTileLayer holds significant WebGL/GPU resources (tile decode
			// contexts, sprite textures). map.remove() only detaches from the
			// layers collection — destroy() is required to release the resources.
			// Without this, repeated mount/unmount of map components (e.g. asset
			// details inset map) leaks GL contexts and eventually exhausts the
			// browser's per-process WebGL context limit, breaking maps in all tabs.
			vtl.destroy?.();
		});
		this._vectorTileLayersCreated = this._vectorTileLayersCreated.filter(
			vtl => newSourceIds.has(vtl.id),
		);

		// Collect NEW-VTL sprite builds and resolve in parallel. Each
		// _createVtlWithPrebuiltSprite call awaits its own sprite atlas build
		// + layerView + setSpriteSource; running them in parallel rather than
		// serially means N tile sources start their sprite work simultaneously
		// instead of stacking. Existing-VTL (REUSE) work stays synchronous.
		const newVtlPromises: Promise<VectorTileLayer>[] = [];

		for (const sourceItem of vectorTileSourceItems) {
			const sourceIds = Object.keys(sourceItem);
			const sourceId = sourceIds.length > 0 ? sourceIds[0] : undefined;
			if (!sourceId) {
				logger.error(
					'No source ID found in source Object. Skipping layer.',
				);
				return;
			}
			if (sourceIds.length > 1) {
				logger.error(
					'Multiple sources found in source Object, only expecting one. Skipping layer.',
				);
				return;
			}
			const matchingLayers = this._layers.filter(lyr =>
				sourceIds.includes(lyr.source),
			);
			const styleLayers: any[] = [];
			// reversing order to see points on top of lines.
			matchingLayers.reverse();
			matchingLayers.forEach(lyr => {
				// Bake the current visibility state into the style spec so that
				// loadStyle() always produces the correct visible/hidden state.
				// setStyleLayer() is still used for immediate toggle feedback, but
				// each style rebuild now reflects CompositeLayer.active as well.
				const visibility: 'visible' | 'none' = lyr.active
					? 'visible'
					: 'none';

				let styleLayer = { ...lyr.styleLayer };
				styleLayer.layout = {
					...(styleLayer.layout ?? {}),
					visibility,
				};
				// Seed the base-style snapshot with the PRISTINE (unresolved)
				// style layer before any build-time rewrite below. This keeps
				// restoreLayerToBaseStyle (theme switch / heatmap toggle-off)
				// reverting to the true asset icon — not to a baked-in glow.
				lyr.seedBaseSnapshot(styleLayer.id, styleLayer);
				// Bake the resolved heatmap "bubble" glow into the built style (if
				// this layer has a pending bubble edit) so loadStyle never paints
				// the asset icon first — eliminating the icon flicker and the
				// save-revert. The resolver produces the fully-coloured glow.
				const resolved = this._sensorStyleResolver?.(lyr.id, styleLayer);
				if (resolved) styleLayer = resolved;

				// Polygon layers build a separate line style layer for the
				// outline (LayerBase.ts setup()); push it right after its fill
				// so the border renders on top of it.
				const secondStyle = lyr.styleLayerSecond
					? { ...lyr.styleLayerSecond }
					: undefined;
				if (secondStyle) {
					secondStyle.layout = {
						...(secondStyle.layout ?? {}),
						visibility,
					};
				}

				const selectedStyle = lyr.styleLayerSelected
					? { ...lyr.styleLayerSelected }
					: undefined;
				if (selectedStyle) {
					if (!selectedStyle.filter) {
						selectedStyle.filter = ['==', 1, 0];
					}
					selectedStyle.layout = {
						...(selectedStyle.layout ?? {}),
						visibility,
					};
				}

				const highlightedStyle = lyr.styleLayerHighlighted
					? { ...lyr.styleLayerHighlighted }
					: undefined;
				// add the layers in reverse for symbols
				if (highlightedStyle) {
					if (!highlightedStyle.filter) {
						highlightedStyle.filter = ['==', 1, 0];
					}
					highlightedStyle.layout = {
						...(highlightedStyle.layout ?? {}),
						visibility,
					};
				}
				styleLayers.push(styleLayer);
				if (secondStyle) {
					styleLayers.push(secondStyle);
				}
				if (selectedStyle) {
					styleLayers.push(selectedStyle);
				}
				if (highlightedStyle) {
					styleLayers.push(highlightedStyle);
				}
			});
			if (styleLayers.length === 0) continue;

			const style = {
				version: 8,
				sources: sourceItem,
				layers: styleLayers,
			};

			updateStlyeLabelConfig(style);

			const existingLayer = this._mapView.map?.findLayerById(sourceId);
			if (existingLayer && existingLayer instanceof VectorTileLayer) {
				this._applyStyleAndSprites(
					existingLayer,
					style,
					true,
					matchingLayers,
				);
				matchingLayers.forEach(compositeLayer => {
					compositeLayer.vectorTileLayer = existingLayer;
				});
			} else {
				// NEW VTL — pre-build the sprite atlas and setSpriteSource
				// before any tile is allowed to paint. Otherwise Esri creates
				// the VTL, begins fetching + rendering tiles immediately, and
				// symbol layers paint with no icons (sprite atlas is still
				// being built). A later `vtl.refresh()` after `setSpriteSource`
				// does not reliably redraw symbol layers on already-painted
				// tiles, which surfaces as intermittent missing manholes on
				// the inset map.
				newVtlPromises.push(
					this._createVtlWithPrebuiltSprite(
						sourceId,
						style,
						matchingLayers,
					),
				);
			}
		}

		if (newVtlPromises.length > 0) {
			const newVtls = await Promise.all(newVtlPromises);
			this._vectorTileLayersCreated.push(...newVtls);
		}
	}

	/**
	 * Converts an HTMLCanvasElement to an HTMLImageElement via a Blob URL.
	 *
	 * The Blob URL approach also ensures each call
	 * produces a uniquely-addressed image that is never served from the browser's
	 * response cache, preventing stale decoded-image reuse.
	 */
	private _canvasToImage(canvas: HTMLCanvasElement): Promise<HTMLImageElement> {
		return new Promise((resolve, reject) => {
			canvas.toBlob(blob => {
				if (!blob) {
					reject(new Error('canvas.toBlob produced null'));
					return;
				}
				const blobUrl = URL.createObjectURL(blob);
				const img = new Image();
			img.onload = () => {
				URL.revokeObjectURL(blobUrl);
				resolve(img);
			};
				img.onerror = () => {
					URL.revokeObjectURL(blobUrl);
					reject(new Error('HTMLImageElement failed to load from blob URL'));
				};
				img.src = blobUrl;
			}, 'image/png');
		});
	}

	/**
	 * Creates a new VectorTileLayer with its sprite atlas pre-built so the
	 * first tile paint includes symbol icons. The flow is:
	 *   1. Build sprite atlas from the static style layers (await).
	 *   2. Create the VTL with `visible: false` so Esri does not paint.
	 *   3. Add to map and wait for the layerView (bounded by timeout).
	 *   4. `setSpriteSource` (await) — sprite is in place.
	 *   5. Set `visible: true` — first paint uses the sprite.
	 *
	 * The original NEW-VTL flow created the VTL `visible:true`, kicked off
	 * `setSpriteSource` asynchronously, and called `vtl.refresh()` afterwards.
	 * That left a race where symbol layers could paint with an empty sprite,
	 * and a later refresh did not consistently re-paint them — surfacing as
	 * intermittent missing manholes on the asset-details inset map.
	 *
	 * The whenOnce wait is bounded by `CREATE_VTL_WHEN_ONCE_TIMEOUT_MS` so a
	 * stuck tile fetch can't leave the VTL invisible forever — on timeout we
	 * proceed to setSpriteSource and reveal the layer, accepting a brief
	 * sprite-after-paint race in exchange for liveness.
	 */
	private async _createVtlWithPrebuiltSprite(
		sourceId: string,
		style: object,
		matchingLayers: CompositeLayer[],
	): Promise<VectorTileLayer> {
		const CREATE_VTL_WHEN_ONCE_TIMEOUT_MS = 3000;

		const staticLayers =
			((style as { layers?: unknown[] }).layers as Array<{
				type?: string;
			}>) ?? [];
		const hasSymbolLayers = staticLayers.some(l => l.type === 'symbol');

		// Build sprite ahead of layer creation so it's ready to set before the
		// first paint. Only required when the style declares symbol layers.
		let spriteSourceInfo: SpriteSourceImageInfo | undefined = undefined;
		if (hasSymbolLayers) {
			spriteSourceInfo =
				await VectorTileStyleToEsriRenderer.createSpriteSourceImageInfoForStyleLayers(
					staticLayers,
					this._symbolLoaderForSprites(),
				);
		}

		const vtl = new VectorTileLayer({
			id: sourceId,
			visible: false,
			style,
		});
		const seq = (this._vtlLoadCounters.get(vtl) ?? 0) + 1;
		this._vtlLoadCounters.set(vtl, seq);
		this._mapView.map?.add(vtl);
		matchingLayers.forEach(compositeLayer => {
			compositeLayer.vectorTileLayer = vtl;
		});

		try {
			const layerView = await this._mapView.whenLayerView(vtl);
			// Bounded wait — whenOnce(!updating) can otherwise hang forever if
			// tile fetches stall, leaving the VTL invisible permanently.
			await Promise.race([
				whenOnce(() => !layerView.updating).catch(() => undefined),
				new Promise<void>(resolve =>
					setTimeout(resolve, CREATE_VTL_WHEN_ONCE_TIMEOUT_MS),
				),
			]);

			if (this._vtlLoadCounters.get(vtl) !== seq) {
				vtl.visible = true;
				return vtl;
			}

			if (spriteSourceInfo) {
				await vtl.setSpriteSource({
					...spriteSourceInfo,
					spriteUrl: 'local://sprites/',
				});
				if (this._vtlLoadCounters.get(vtl) !== seq) {
					vtl.visible = true;
					return vtl;
				}
			}

			matchingLayers.forEach(layer => layer.refreshLayerVisibility());
			matchingLayers.forEach(compositeLayer => {
				if (compositeLayer.selectedItems.length > 0) {
					compositeLayer.selectedItems = compositeLayer.selectedItems;
				}
			});
			vtl.visible = true;
			this._onFinalStyleLoaded?.();
		} catch (err) {
			logger.error(`Pre-create sprite setup failed for ${vtl.id}`, {
				error: err,
			});
			vtl.visible = true;
		}

		return vtl;
	}

	private _applyStyleAndSprites(
		vtl: VectorTileLayer,
		style: object,
		loadStyle: boolean,
		compositeLayers?: CompositeLayer[],
	): void {
		// Increment the sequence counter for this VTL before entering the async
		// block so we can detect whether a newer loadStyle was started after us.
		const seq = (this._vtlLoadCounters.get(vtl) ?? 0) + 1;
		this._vtlLoadCounters.set(vtl, seq);

		this._mapView.when(async () => {
			try {
				if (loadStyle) {
					// A newer _applyStyleAndSprites call has already been scheduled
					// for this VTL — skip the loadStyle here so the stale bake
					// cannot overwrite the up-to-date style that the newer call
					// will apply. (_vtlLoadCounters is incremented synchronously
					// before the mapView.when() callback fires, so by the time any
					// async block runs, the counter reflects all pending calls.)
					if (this._vtlLoadCounters.get(vtl) !== seq) return;
					const s = style as { name?: string; layers?: unknown[] };
					logVectorTileLayerStyleCall('loadStyle', vtl, {
						styleName: s.name,
						layerCount: Array.isArray(s.layers)
							? s.layers.length
							: undefined,
					});
					await vtl.loadStyle(style);
					// loadStyle resets the atlas to the theme's default sprite —
					// drop the cached sprite-id set so a stale entry can't make a
					// later `ensure` wrongly skip (which would blank the icons).
					this._vtlAtlasSpriteIds.delete(vtl);
				}
				const layerView = await this._mapView.whenLayerView(vtl);
				await whenOnce(() => !layerView.updating);
				// Only re-apply visibility if no newer loadStyle call was started
				// after this one. If a newer call is in flight, getStyleLayer()
				// returns null (style has been cleared) and we'd silently lose the
				// update. The newer call will apply visibility once it settles.
				if (this._vtlLoadCounters.get(vtl) === seq) {
					compositeLayers?.forEach(layer =>
						layer.refreshLayerVisibility(),
					);
					// After loadStyle(), Esri clears the tile cache but does not
					// always re-request tiles for the current viewport — leaving the
					// view blank until the user pans or zooms. Force a refresh so
					// tiles are immediately re-fetched for the current view.
					if (loadStyle) {
						logVectorTileLayerStyleCall('refresh', vtl, {
							reason: 'afterLoadStyle',
						});
						vtl.refresh();
					}
					// Re-apply the authoritative visibility from the hook so any
					// toggle that fired while loadStyle was in flight is not lost.
					this._onFinalStyleLoaded?.();
				}
				// Re-apply any pre-existing selection filters now that the VTL style
				// has loaded (loadStyle resets all filters to the initial empty values).
				compositeLayers?.forEach(compositeLayer => {
					if (compositeLayer.selectedItems.length > 0) {
						compositeLayer.selectedItems =
							compositeLayer.selectedItems;
					}
				});
				const staticLayers =
					(style as { layers?: unknown[] }).layers ?? [];
				if (!staticLayers.some((layer: any) => layer.type === 'symbol')) {
					return;
				}

				// Read the CURRENT icon-image values from the live VTL style —
				// these reflect any overrides re-applied above after loadStyle.
				const currentStyleLayers = this._collectCurrentVtlStyleLayers(vtl);
				const layersForSprites =
					currentStyleLayers.length > 0 ? currentStyleLayers : staticLayers;

				const spriteSourceInfo =
					await VectorTileStyleToEsriRenderer.createSpriteSourceImageInfoForStyleLayers(
						layersForSprites,
						this._symbolLoaderForSprites(),
					);



				await vtl.setSpriteSource({
					...spriteSourceInfo,
					spriteUrl: 'local://sprites/',
				});
				// Record the freshly-baked atlas content so a subsequent colour-by
				// `ensure` can skip a redundant rebuild + refresh.
				this._vtlAtlasSpriteIds.set(
					vtl,
					new Set(
						VectorTileStyleToEsriRenderer.collectSpriteIconIdsFromStyleLayers(
							layersForSprites,
						),
					),
				);
				this._onFinalStyleLoaded?.();
				vtl.refresh();

				logger.debug(
					`loadStyle + setSpriteSource succeeded for ${vtl.id}`,
				);
			} catch (error: unknown) {
				logger.error(`Style/sprite application failed for ${vtl.id}`, {
					error,
				});
			}
		});
	}

	/**
	 * Collects the currently-active symbol style layers from a VectorTileLayer
	 * by querying `getStyleLayer` for each known composite layer id and its
	 * variants. Returns live values so the sprite sheet always reflects the
	 * latest `icon-image` settings (e.g. after a theme color change).
	 */
	private _collectCurrentVtlStyleLayers(vtl: VectorTileLayer): any[] {
		const vtlSourceId = vtl.id;
		const vtlCompositeLayers = this._layers.filter(
			l => l.source === vtlSourceId,
		);
		const knownPostfixes = [
			'',
			'-selected',
			'-highlight-background',
			'-highlight-foreground',
			'-second',
		];
		const result: any[] = [];
		for (const compositeLayer of vtlCompositeLayers) {
			for (const postfix of knownPostfixes) {
				const id = compositeLayer.id + postfix;
				const sl = (vtl as any).getStyleLayer?.(id);
				if (sl) result.push(sl);
			}
		}
		return result;
	}

	/**
	 * Regenerates and applies the sprite sheet for the VectorTileLayer that
	 * owns the given `layerId`. Reads the *current* icon-image values from the
	 * live VTL style so the sheet reflects any theme color/icon changes applied
	 * via `setStyleLayer` since the last sprite build.
	 *
	 * Calls are serialised per-VTL: if a refresh is already in flight when a
	 * new request arrives, the new request is queued as "pending".  The
	 * in-flight call re-runs automatically once it settles, using the latest
	 * VTL state at that point.  This prevents concurrent `setSpriteSource` +
	 * `vtl.refresh()` calls which cause Esri's TileQueue AbortErrors.
	 */
	async refreshSpritesForVtlContainingLayer(layerId: string): Promise<void> {
		const vtl = this._findLoadedVtlForLayer(layerId);
		if (!vtl) return;

		// Serialise: if a refresh is already running for this VTL, mark it as
		// pending so it re-runs once the current one settles.
		const queued = this._vtlSpriteRefreshQueue.get(vtl);
		if (queued) {
			queued.pending = true;
			return queued.promise;
		}

		const entry: { promise: Promise<void>; pending: boolean } = { promise: Promise.resolve(), pending: false };
		entry.promise = this._doRefreshSpritesForVtl(vtl).finally(() => {
			this._vtlSpriteRefreshQueue.delete(vtl);
			if (entry.pending) {
				this.refreshSpritesForVtlContainingLayer(layerId);
			}
		});
		this._vtlSpriteRefreshQueue.set(vtl, entry);
		return entry.promise;
	}

	/**
	 * Ensures the sprite atlas of the VTL owning `layerId` contains every sprite
	 * referenced by the supplied synthetic style layers (e.g. a symbol layer
	 * whose `icon-image` is the about-to-be-applied data-driven colour-by
	 * expression) BEFORE the caller flips the live `icon-image`. This makes the
	 * symbol colour-by transition sprite→sprite with no blank frame.
	 *
	 * Idempotent: when every required sprite id is already in the atlas it skips
	 * the rebuild + repaint entirely, so the host's burst of re-applies
	 * (after-add / style-loaded / lookup-complete) collapses to a single swap.
	 * Shares the per-VTL serialisation queue with
	 * {@link refreshSpritesForVtlContainingLayer}. Does NOT call `vtl.refresh()`
	 * (`refreshAfter = false`): `setSpriteSource` + the caller's subsequent
	 * `setStyleLayer` already repaint the icons, so the extra tile re-fetch would
	 * only add a visible flash.
	 */
	async ensureSpritesForStyleLayers(
		layerId: string,
		syntheticStyleLayers: any[],
	): Promise<void> {
		const vtl = this._findLoadedVtlForLayer(layerId);
		if (!vtl) return;

		const styleLayers = [
			...this._collectCurrentVtlStyleLayers(vtl),
			...syntheticStyleLayers,
		];

		// Skip when the atlas already holds every sprite the target references —
		// the band colours are deterministic, so after the first build all later
		// re-applies are cache hits (no setSpriteSource, no repaint).
		const required =
			VectorTileStyleToEsriRenderer.collectSpriteIconIdsFromStyleLayers(
				styleLayers,
			);
		const present = this._vtlAtlasSpriteIds.get(vtl);
		if (present && required.every(id => present.has(id))) return;

		const queued = this._vtlSpriteRefreshQueue.get(vtl);
		if (queued) {
			queued.pending = true;
			return queued.promise;
		}

		const entry: { promise: Promise<void>; pending: boolean } = { promise: Promise.resolve(), pending: false };
		entry.promise = this._buildAndSetSprites(vtl, styleLayers, false).finally(() => {
			this._vtlSpriteRefreshQueue.delete(vtl);
			if (entry.pending) {
				this.ensureSpritesForStyleLayers(layerId, syntheticStyleLayers);
			}
		});
		this._vtlSpriteRefreshQueue.set(vtl, entry);
		return entry.promise;
	}

	/**
	 * Finds the loaded VectorTileLayer that owns the given style `layerId`, or
	 * null. Skips VTLs that haven't finished loading — calling `getStyleLayer` on
	 * an unloaded VTL crashes because Esri's internal `_style` object is undefined.
	 */
	private _findLoadedVtlForLayer(layerId: string): VectorTileLayer | null {
		const mapLayers = this._mapView.map?.layers?.toArray() ?? [];
		for (const layer of mapLayers) {
			if (!(layer instanceof VectorTileLayer)) continue;
			if (!layer.loaded) continue;
			if ((layer as any).getStyleLayer?.(layerId)) {
				return layer as VectorTileLayer;
			}
		}
		return null;
	}

	/**
	 * Internal: performs the actual sprite rebuild + setSpriteSource for `vtl`,
	 * reading the *current* live style layers. Should only be called from
	 * `refreshSpritesForVtlContainingLayer`.
	 */
	private async _doRefreshSpritesForVtl(vtl: VectorTileLayer): Promise<void> {
		// Yield to the microtask queue before snapshotting.  This ensures all
		// synchronous VTL mutations that were triggered in the same call frame
		// (e.g. setFixedColor followed immediately by setIcon in onLayerEdit)
		// have completed before the style-layer snapshot is taken and the sprite
		// atlas is built.  Without this, the snapshot would be captured between
		// the two mutations, producing an atlas with only the first change.
		await Promise.resolve();
		return this._buildAndSetSprites(vtl, this._collectCurrentVtlStyleLayers(vtl));
	}

	/**
	 * Internal: builds the sprite atlas from the supplied style layers and swaps
	 * it onto `vtl` via `setSpriteSource`. Calls `vtl.refresh()` only when
	 * `refreshAfter` (default true) — the atlas-first ensure path passes false
	 * because the caller's `setStyleLayer` already repaints. Shared by the
	 * current-style bulk refresh (`_doRefreshSpritesForVtl`) and the targeted
	 * ensure (`ensureSpritesForStyleLayers`).
	 */
	private async _buildAndSetSprites(
		vtl: VectorTileLayer,
		currentStyleLayers: any[],
		refreshAfter = true,
	): Promise<void> {
		const symbolCount = currentStyleLayers.filter((sl: any) => sl.type === 'symbol').length;
		if (symbolCount === 0) return;

		try {
			const spriteSourceInfo =
				await VectorTileStyleToEsriRenderer.createSpriteSourceImageInfoForStyleLayers(
					currentStyleLayers,
					this._symbolLoaderForSprites(),
				);

			// Convert HTMLCanvasElement → HTMLImageElement (via Blob URL) before
			// passing to setSpriteSource. Esri's API expects an HTMLImageElement.
			const raw1x = (spriteSourceInfo as any)?.spriteSource1x?.image as HTMLCanvasElement | undefined;
			const raw2x = (spriteSourceInfo as any)?.spriteSource2x?.image as HTMLCanvasElement | undefined;
			const [img1xEl, img2xEl] = await Promise.all([
				raw1x && raw1x.width > 1 ? this._canvasToImage(raw1x) : Promise.resolve(raw1x as any),
				raw2x && raw2x.width > 1 ? this._canvasToImage(raw2x) : Promise.resolve(raw2x as any),
			]);

			const uniqueSpriteSourceInfo = {
				...spriteSourceInfo,
				spriteUrl: 'local://sprites/',
				spriteSource1x: { ...(spriteSourceInfo as any).spriteSource1x, image: img1xEl },
				spriteSource2x: { ...(spriteSourceInfo as any).spriteSource2x, image: img2xEl },
			};

			// Wait for layer to be ready before calling setSpriteSource.
			const layerView = await this._mapView.whenLayerView(vtl);
			await whenOnce(() => !layerView.updating);

			await vtl.setSpriteSource(uniqueSpriteSourceInfo);

			// Record what the atlas now holds so subsequent `ensure` calls can
			// skip a redundant rebuild + refresh when their sprites are present.
			this._vtlAtlasSpriteIds.set(
				vtl,
				new Set(
					VectorTileStyleToEsriRenderer.collectSpriteIconIdsFromStyleLayers(
						currentStyleLayers,
					),
				),
			);

			if (refreshAfter) {
				logVectorTileLayerStyleCall('refresh', vtl, {
					reason: 'afterSetSpriteSource',
				});
				vtl.refresh();
			}

			logger.debug(`_buildAndSetSprites succeeded for ${vtl.id}`);
		} catch (error: unknown) {
			logger.error(`_buildAndSetSprites failed for ${vtl.id}`, { error });
		}
	}

	/**
	 * GeoJSON sources can publish a polygon as two style layers: fill + line
	 * (`-second`). Mapbox renders both; Esri GeoJSON renderer receives one layer.
	 * Merge the line stroke paint into the fill layer so outline color/width/
	 * opacity match Mapbox visual output.
	 */
	private _mergeGeoJsonFillWithOutlineFromLine(
		fillLayer: FillLayerSpecification,
		sourceStyleLayers: LayerSpecification[],
	): FillLayerSpecification {
		const lineLayer =
			(sourceStyleLayers.find(
				layer =>
					layer.type === 'line' && layer.id === `${fillLayer.id}-second`,
			) as LineLayerSpecification | undefined) ??
			(sourceStyleLayers.find(
				layer => layer.type === 'line',
			) as LineLayerSpecification | undefined);

		if (!lineLayer) {
			return fillLayer;
		}

		const lineColor = lineLayer.paint?.['line-color'];
		const lineWidth = lineLayer.paint?.['line-width'];
		const lineOpacity = lineLayer.paint?.['line-opacity'];

		return {
			...fillLayer,
			paint: {
				...(fillLayer.paint ?? {}),
				...(typeof lineColor === 'string'
					? { 'fill-outline-color': lineColor }
					: {}),
				...(typeof lineWidth === 'number'
					? {
							'fill-outline-width': lineWidth,
						}
					: {}),
				...(typeof lineOpacity === 'number'
					? {
							'fill-outline-opacity': lineOpacity,
						}
					: {}),
			} as FillLayerSpecification['paint'],
		};
	}

	private async _updateGeoJsonLayers(
		styleLayers: any[],
		styleSources: any[],
	): Promise<void> {
		const symbolLoader = this._symbolLoaderForSprites();
		for (const source of styleSources) {
			try {
				const sourceId = Object.keys(source)[0];
				const sourceStyleLayers = styleLayers.filter(
					({ source }) => source === sourceId,
				) as LayerSpecification[];
				const baseLayerSpec =
					sourceStyleLayers.find(layer => layer.type === 'fill') ??
					sourceStyleLayers[0];
				if (!baseLayerSpec) {
					continue;
				}
				const layerSpec =
					baseLayerSpec.type === 'fill'
						? this._mergeGeoJsonFillWithOutlineFromLine(
								baseLayerSpec as FillLayerSpecification,
								sourceStyleLayers,
						  )
						: baseLayerSpec;
				const layerId = layerSpec?.id;
				if (!layerId) {
					continue;
				}

				let mapLayer = this._mapView.map?.findLayerById(layerId);
				const geoJsonData = source[sourceId]?.data;

				// GeoJSONLayer does not support data mutation after construction.
				// If the layer already exists and its data reference has changed, remove it
				// so the block below recreates it with the latest data.
				if (
					mapLayer instanceof GeoJSONLayer &&
					geoJsonData !== this._geoJsonLayerData.get(layerId)
				) {
					this._mapView.map?.remove(mapLayer);
					mapLayer = undefined;
				}

				if (!mapLayer) {
					if (!geoJsonData?.features?.length) {
						logger.debug(`layer ${layerId} has no features`);
						continue;
					}
					this._geoJsonLayerData.set(layerId, geoJsonData);
					const blob = new Blob([JSON.stringify(geoJsonData)], {
						type: 'application/json',
					});
					const url = URL.createObjectURL(blob);
					const renderer =
						await VectorTileStyleToEsriRenderer.convert(
							layerSpec,
							source[sourceId]?.data as GeoJsonDataType,
							symbolLoader,
						);
					const labelClass = layerToLabelClass(layerSpec);
					const labelingInfo = labelClass ? [labelClass] : [];
					const compositeLayer = this._layers.find(
						layer => layer.id === layerId,
					);
					const geoJSONLayer = new GeoJSONLayer({
						url,
						id: layerId,
						title: compositeLayer?.displayName,
						objectIdField: 'id',
						renderer: renderer ?? undefined,
						labelingInfo: labelingInfo,
						minScale: layerSpec.minzoom
							? zoomToScale(layerSpec.minzoom)
							: undefined,
						maxScale: layerSpec.maxzoom
							? zoomToScale(layerSpec.maxzoom)
							: undefined,
					});
					if (compositeLayer) {
						this._mapView.map?.add(geoJSONLayer);
						this._mapView
							.whenLayerView(geoJSONLayer)
							.then(layerView => {
								compositeLayer.geoJsonLayerView = layerView;
								// Re-apply the active state now that the layer view exists.
								// If the user toggled visibility before whenLayerView settled,
								// hide()/show() silently skipped the geoJsonLayerView assignment
								// (null check). Without this call the visual state never catches up.
								compositeLayer.refreshLayerVisibility();
							});
					}
				} else if (mapLayer instanceof GeoJSONLayer) {
					// Data is unchanged — only update renderer in place (e.g. theme changed).
					const renderer =
						await VectorTileStyleToEsriRenderer.convert(
							layerSpec,
							source[sourceId]?.data as GeoJsonDataType,
							symbolLoader,
						);
					if (renderer != null) {
						mapLayer.renderer = renderer;
					}
				}
			} catch (e) {
				console.error('Error adding GeoJSON layer', e);
			}
		}
	}

	private _update(layerId: string, layerInfo: CompositeLayerProps) {
		const existingLayer = this.getLayerById(layerId);
		if (existingLayer) {
			existingLayer.layerInfo = layerInfo;
		}
	}

	private _deleteNotFound(layers: CompositeLayerProps[], serviceId?: string) {
		const existingLayers = serviceId
			? this.getByServiceId(serviceId)
			: this.compositeLayers;
		const layerIds = layers.map(mapToId);
		const [, notFound] = partition(existingLayers, ({ id }) =>
			layerIds.includes(id),
		);
		notFound.forEach(layer => layer.delete());
		this._layers = this._layers.filter(
			({ id }) => !notFound.map(mapToId).includes(id),
		);
	}

	deleteAll(): void {
		this._layers.forEach(layer => layer.delete());
		this._layers = [];
		// Also tear down any VectorTileLayers we created. See the note in
		// _updateVectorTileLayers — map.remove() alone leaks the GL context,
		// which surfaces as cross-tab map breakage on heavy mount/unmount
		// (asset details inset map).
		this._vectorTileLayersCreated.forEach(vtl => {
			this._mapView?.map?.remove(vtl);
			vtl.destroy?.();
		});
		this._vectorTileLayersCreated = [];
		this._vtlLoadCounters.clear();
		this._vtlSpriteRefreshQueue.clear();
	}

	deleteByServiceId(serviceId: string): void {
		const layersForService = this._allLayersForServiceId(serviceId);
		const layerIdsToDelete = layersForService.map(mapToId);
		layersForService.forEach(layer => layer.delete());
		this._layers = this._layers.filter(
			({ id }) => !layerIdsToDelete.includes(id),
		);
	}

	toggleLayer(
		layerId: CompositeLayerProps['id'],
		newState?: boolean,
	): boolean | undefined {
		const layer = this.getLayerById(layerId);
		if (!layer) return;
		layer.toggleLayerVisibility(newState);
		return layer.active;
	}

	/**
	 * Re-applies each layer's current `active` state to its visual representation
	 * without any guard. Use this after a `loadStyle` completes to fix silent
	 * visual-update failures: Esri's `getStyleLayer` returns null while a style
	 * is loading, so `show()`/`hide()` calls made during that window are no-ops
	 * even though `active` was already set correctly.
	 */
	forceRefreshAllVisibility(): void {
		this._layers.forEach(layer => layer.refreshLayerVisibility());
	}

	setLayerVisibility(hiddenLayers: MapHiddenLayers | LayerVisibility): void {
		const hiddenLayerIds = Array.isArray(hiddenLayers)
			? hiddenLayers.map(layer => getLayerIdFromIdPath(layer))
			: Object.entries(hiddenLayers).flatMap(([key, value]) =>
					value === false ? [getLayerIdFromIdPath(key)] : [],
				);
		// When hiddenLayers is an object map, only update layers explicitly
		// listed as keys. Layers absent from the map are internal/secondary
		// layers (e.g. color-by-attribute duplicates) whose state must not be
		// inferred from "not in hiddenLayerIds" — that would incorrectly mark
		// them as changed and trigger unnecessary VTL refreshes on unrelated sources.
		const explicitLayerIds = !Array.isArray(hiddenLayers)
			? new Set(
					Object.keys(hiddenLayers).map(k => getLayerIdFromIdPath(k)),
				)
			: null;
		const changedLayerIds = this._layers
			.filter(layer => {
				if (explicitLayerIds && !explicitLayerIds.has(layer.id))
					return false;
				return layer.active !== !hiddenLayerIds.includes(layer.id);
			})
			.map(layer => layer.id);

		this._layers.forEach(layer => {
			if (explicitLayerIds && !explicitLayerIds.has(layer.id)) return;
			const visible = !hiddenLayerIds.includes(layer.id);
			if (layer.active !== visible) {
				layer.toggleLayerVisibility(visible);
			}
		});

		// Ask Esri to redraw only sources that changed visibility.
		// Full _updateEsriLayers() on every toggle causes extra loadStyle races.
		if (changedLayerIds.length > 0) {
			const changedSources = Array.from(
				new Set(
					this._layers
						.filter(layer => changedLayerIds.includes(layer.id))
						.map(layer => layer.source)
						.filter(Boolean),
				),
			);
			changedSources.forEach(sourceId => {
				const sourceLayers = this._layers.filter(
					layer => layer.source === sourceId,
				);
				const hasAnyVisibleLayer = sourceLayers.some(layer =>
					Boolean(layer.active),
				);
			const mapLayer = this._mapView.map?.findLayerById(sourceId);
			if (mapLayer instanceof VectorTileLayer) {
				// Only force a source-level redraw when aggregate source visibility
				// actually changes. Refreshing on every child-layer toggle can trigger
				// unnecessary tile reloads and intermittent blank states at high zoom.
				if (mapLayer.visible !== hasAnyVisibleLayer) {
					mapLayer.visible = hasAnyVisibleLayer;
					if (hasAnyVisibleLayer) {
						logVectorTileLayerStyleCall('refresh', mapLayer, {
							reason: 'sourceVisibilityRestored',
							sourceId,
						});
						mapLayer.refresh();
					}
				}
			}
		});
		}
	}

	clearSelectedLayers(): void {
		this._layers.forEach(layer => layer.clearSelectedItems());
	}

	redrawByServiceId(
		serviceId: string,
		loaded: boolean,
		iconSet: IconSet,
	): void {
		const layersForService = this._allLayersForServiceId(serviceId);
		layersForService.forEach(layer => {
			layer.iconSet = iconSet;
			layer.loaded = loaded;
		});
	}

	setSelectedItemsOnLayer(
		layerId: CompositeLayerProps['id'],
		ids: string[],
	): void {
		const layer = this.getLayerById(layerId);
		if (!layer) {
			this._pendingSelections.set(layerId, ids);
			return;
		}
		layer.selectedItems = ids;
	}

	setHighlightedItemsOnLayer(
		layerId: CompositeLayerProps['id'],
		ids: string[],
	): void {
		const layer = this.getLayerById(layerId);
		if (!layer) {
			this._pendingHighlights.set(layerId, ids);
			return;
		}
		layer.highlightedItems = ids;
	}

	clearHighlightedLayers(): void {
		this._layers.forEach(layer => layer.clearHighlightedItems());
		this._pendingHighlights.clear();
	}

	getLayerById(
		layerId: CompositeLayerProps['id'],
	): CompositeLayer | undefined {
		return this._layers.find(({ id }) => id === layerId);
	}

	getLayerByAttributesId(attributeId: string): CompositeLayer | undefined {
		return this._layers.find(
			l => l.layerInfo.attributeLayerId === attributeId,
		);
	}

	private _allLayersForServiceId(toFindServiceId: string): CompositeLayer[] {
		return this._layers.filter(
			({ serviceId }) => serviceId === toFindServiceId,
		);
	}
}

function queryAssetLayer(
	{ layerInfo }: Pick<CompositeLayer, 'layerInfo'>,
	systemType: string,
	assetType: string,
): boolean {
	return (
		layerInfo.systemType === systemType && layerInfo.assetType === assetType
	);
}

function normalizeSourceLayer(name: string): string {
	return name.replace(/[\W_]+/g, '').toLowerCase();
}

function querySpatialLayer(
	{ layerInfo }: Pick<CompositeLayer, 'layerInfo'>,
	layerName: string,
): boolean {
	return (
		layerInfo.source === 'tiles-other' &&
		layerInfo.sourceLayer === normalizeSourceLayer(layerName)
	);
}

function queryRiskLayer(
	{ layerInfo }: Pick<CompositeLayer, 'layerInfo'>,
	riskConfig: string,
): boolean {
	return (
		layerInfo.source === `Risk/${riskConfig}` &&
		layerInfo.sourceLayer === riskConfig.replace(/-/g, '')
	);
}
